import { Format } from "../core/elements/format"
import { Margins } from "../core/elements/margins"
import { Shadow } from "../core/elements/shadow"
import { Point, Rectangle } from "../core/primitives"

// !MIGRACION - ELIMINAR TRAS USAR MODULES
const obDefP = Object.defineProperty

/**
 * @returns {Number} Returns the integer part of value, without decimals, rounded to lower.
 */
function trunc(value) {
	return value | 0
}

function isSeriesTarget(target) {
	return !!(
		target &&
		target.data &&
		Array.isArray(target.data.values) &&
		typeof target.clicked === "function"
	)
}

/**
 * @constructor
 * @class Abstract base class to represent a tool.
 * @param {Tee.Chart} chart The parent chart this tool belongs to.
 * @property {Boolean} [active=true] Determines if this tool will be painted or enabled.
 * !MIGRACION: To class after migration inherit functions
 */
let Tool = function Tool(chart) {
	this.chart = chart
	this.active = true
}

Tool.prototype.mousedown = function mousedown() {}
Tool.prototype.mousemove = function mousemove() {}
Tool.prototype.mouseout = function mouseout() {}
Tool.prototype.clicked = function clicked() {
	return false
}
Tool.prototype.draw = function draw() {}

/**
 * @constructor
 * @augments Tool
 * @class Base abstract class to perform Animations.
 * @property {Number} [duration=500] Duration in milliseconds of the animation.
 * @property {boolean} [loop=false] When true, the animation never stops (starts again when finished).
 * @property {boolean} [running=false] Read-only, returns if the animation is currently running.
 * @property {Animation[]} items Sub-animations that are executed in parallel with self.
 * @property {boolean} [autoDraw=true] When true, the animation repaints the chart at every step.
 */
let Animation = function (target, onstep) {
	Tool.call(this, target)

	this.active = true
	this.mode = "linear"
	this.duration = 500
	this.items = []

	this.autoDraw = true
	this.loop = false
	this.running = false

	this.onstart = null
	this.onstop = null

	if (target)
		if (target instanceof Tee.Chart) this.chart = target
		else if (target instanceof Tee.Animation) {
			this.chart = target.chart
			target.items.push(this)
		}

	var o = null

	this._dostart = function () {
		this.init = Date.now()

		o.start()
		for (var t = 0, i; ; t++) {
			i = o.items[t]
			if (!i) break
			if (i.active) {
				i.chart = o.chart
				i.start()
			}
		}

		o.chart.draw()
		requestAnimationFrame(this.step, this)
	}

	this.animate = function (chart) {
		if (!this.running) {
			this.running = true

			if (chart) this.chart = chart

			o = this
			this._dostart()
		}
	}

	this.start = function () {
		if (this.onstart) this.onstart()
	}
	this.stop = function () {
		if (this.onstop) this.onstop()
	}

	this.doStep = f => {
		if (onstep) onstep(f)
	}

	this.step = () => {
		var now = Date.now(),
			t,
			i,
			tmp = (now - o.init) / o.duration,
			f = o.mode === "linear" ? tmp : 2 ** (10 * (tmp - 1))

		if (f >= 0 && f < 1) {
			if (o.running) {
				o.doStep(f)

				for (t = 0; ; t++) {
					i = o.items[t]
					if (!i) break
					if (i.active) {
						i.chart = o.chart
						i.doStep(f)
					}
				}

				if (o.autoDraw) o.chart.draw()

				requestAnimationFrame(o.step, o)
			}
		} else {
			o.stop()
			for (t = 0; ; t++) {
				i = o.items[t]
				if (!i) break
				if (i.active) {
					i.chart = o.chart
					i.stop()
				}
			}

			if (o.onstop) o.onstop(o)

			if (o.loop) o._dostart()
			else {
				o.running = false
				o.chart.draw()
			}
		}
	}
}

Animation.prototype = new Tool()

/**
 * @constructor
 * @augments Tool
 * @class Represents a rectangle containing text
 * @param {Tee.Chart} chart The parent chart this annotation belongs to.
 * @param {String} text The text to draw inside the annotation.
 * @param {Number} [x=10] Optional left side position in pixels.
 * @param {Number} [y=10] Optional top side position in pixels.
 * @property {Tee.Margins} margins Properties to control spacing between text and rectangle boundaries.
 * @property {Boolean} visible When true, the annotation is displayed.
 * @property {Boolean} transparent When true, the annotation background is not displayed. Only the text is painted.
 * @property {Format} format Properties to control the annotation background and text appearance.
 */
let Annotation = function (chart, text, x, y) {
	Tool.call(this, chart)

	/**
	 * @property {Point} position Top-left coordinates of annotation rectangle.
	 * @default x:10, y:10
	 */
	this.position = new Point(x || 10, y || 10)

	var m = (this.margins = new Margins())

	this.items = []

	var b = (this.bounds = new Rectangle())
	this.visible = true
	this.transparent = false
	this.text = text || ""
	this.isDom = false
	this.domElement = null
	this.domStyle = "border-radius: 5px;border: 2px solid #faad44;background: #FFF;padding:5px;"
	var f = (this.format = new Format(chart))

	f.font.textAlign = "center"
	f.font.baseLine = "top"
	f.fill = "beige"
	f.round = { x: 4, y: 4 }
	f.stroke.fill = "silver"
	f.shadow.visible = true

	f.depth = 0.05
	f.z = 0.5

	var fontH, thisH, over

	this.getDOMHeight = function () {
		return this.domElement == null ? 0 : this.domElement.offsetHeight
	}
	this.getDOMWidth = function () {
		return this.domElement == null ? 0 : this.domElement.offsetWidth
	}
	this.moveTo = function (x, y) {
		this.position.x = x
		this.position.y = y

		this.resize()
	}

	function isEmpty(str) {
		return !str || 0 === str.length
	}

	this.shouldDraw = function () {
		return this.visible && !isEmpty(this.text)
	}

	this.resize = async function () {
		if (isEmpty(this.text)) return

		f.font.prepare()

		this.rows = this.text.split("\n")
		fontH = f.textHeight(this.text)

		var l = this.rows.length

		thisH = fontH * l + m.top

		var w,
			h = thisH + m.bottom

		if (l > 1) {
			w = 0
			while (l--) w = Math.max(w, f.textWidth(this.rows[l] + "W"))
		} else w = f.textWidth(this.text + "W")

		w += m.left + m.right

		var pos = this.position,
			p = pos.y + thisH,
			t,
			i
		/*
	for(t=0; i=this.items[t++];)
	{
		var bi=i.bounds;
		i.resize();
		h+=bi.height;
		w=Math.max(w,bi.width);
		bi.x=pos.x;
		bi.y=p;
		p+=bi.height;
	}

	for(t=0; i=this.items[t++];)
		i.bounds.width=w-m.right;
		*/
		b.set(pos.x, pos.y, w, h)
	}

	this.add = function (text) {
		var a = new Annotation(this.chart, text)

		this.items.push(a) //[this.items.length]=a;
		a.transparent = true
		return a
	}

	this.doDraw = async function () {
		if (!isEmpty(this.text)) {
			if (this.isDom) {
				await this.drawDOMText()
			} else {
				if (this.transparent) this.chart.ctx.z = f.z
				else if (this.chart.aspect.view3d && this.format.depth > 0) {
					var oldz = f.z
					f.z -= this.format.depth * 0.5
					f.cube(b)
					f.draw(this.chart.ctx, null, b)
					f.z = oldz
				} else {
					this.chart.ctx.z = f.z
					f.rectangle(b)
				}

				var old,
					ft = this.format.transparency,
					ctx = this.chart ? this.chart.ctx : null

				if (ft > 0) {
					old = ctx.globalAlpha
					ctx.globalAlpha = (1 - ft) * old
				}

				f.font.prepare()

				b.y += m.top + 0.1 * fontH
				b.x += m.left

				var w = b.width
				b.width -= m.right

				/*
		if (this.transform) {
			ctx.save();
			this.transform(b);
		}
		*/

				f.drawText(b, this.text)

				//if (this.transform) ctx.restore();

				b.x = this.position.x
				b.y = this.position.y
				b.width = w

				if (ft > 0) ctx.globalAlpha = old
			}
			/*
	for(var t=0, i; i=this.items[t++];)
			i.doDraw();
			*/
		}
	}

	/**
	 * @returns {Boolean} Returns if {@link Point} p is inside this Annotation bounds.
	 */
	this.clicked = function (p) {
		return this.visible && b.contains(p) // (&& this.text!="")
	}

	this.doMouseMove = function (p) {
		this.mouseinside = this.clicked(p)

		if (this.mouseinside) {
			if (this.cursor) this.chart.newCursor = this.cursor

			if (!this.wasinside) over = new AnimateHover(250, b, f)
		} else if (this.wasinside) {
			over.restore()
			this.chart.draw()
		}

		this.wasinside = this.mouseinside
	}

	this.mousemove = function (p) {
		if (this.cursor && this.cursor != "default") this.doMouseMove(p)
	}

	this.forceDraw = function () {
		this.resize()
		this.doDraw()
	}

	this.setChart = function (chart) {
		this.chart = chart
		this.format.setChart(chart)
	}

	this.drawDOMText = async function () {
		if (this.domElement) await this.resize() //when awaited, actions a correct update on DOM text (!)

		var rect = this.chart.canvas.getBoundingClientRect()
		var opacity = this.transparent ? "opacity:0;" : "opacity:1;"
		if (!this.domElement) {
			this.domElement = document.createElement("div")
			document.body.appendChild(this.domElement)
		}
		//this.chart.canvas.setAttribute('style', 'z-index:1;');
		if (this.visible == false)
			this.domElement.setAttribute(
				"style",
				"visibility:hidden; position:absolute;top:" +
					(this.position.y + rect.top) +
					"px;left:" +
					(this.position.x + rect.left) +
					"px;display:block;z-index:10000;" +
					this.domStyle +
					opacity
			)
		else
			this.domElement.setAttribute(
				"style",
				"visibility:visible; position:absolute;top:" +
					(this.position.y + rect.top) +
					"px;left:" +
					(this.position.x + rect.left) +
					"px;display:block;z-index:10000;" +
					this.domStyle +
					opacity
			)
		if (this.domElement.innerHTML != this.text) this.domElement.innerHTML = this.text
	}
}

Annotation.prototype = new Tool()

Annotation.prototype.draw = async function () {
	if (this.isDom) await this.drawDOMText()
	else if (this.visible) this.forceDraw()
}

/**
 * @class Draws a glow animation behind the bounds rectangle property.
 * @param {number} duration Animation duration in milliseconds.
 * @param {Rectangle} bounds The rectangle to apply the animation.
 * @param {Format} format The formatting properties to paint the bounds rectangle.
 */
class AnimateHover {
	constructor(duration, bounds, format) {
		this.format = format
		this.bounds = bounds
		this.enabled = true

		const s = format.shadow

		this.old = new Shadow()
		this.old.set(s)

		s.visible = true
		s.color = "rgba(0,255,0,0.1)"
		s.blur = 10
		s.width = 0
		s.height = 0

		const animation = new Animation(format.chart, f => {
			if (!this.enabled) {
				return
			}

			if (f < 1) {
				this.format.shadow.color = `rgba(0,255,0,${f})`
			} else if (this.autoHide) {
				this.restore()
			}
		})

		animation.duration = duration
		animation.animate()
	}

	restore() {
		this.format.shadow.set(this.old)
		this.enabled = false
	}
}

/**
 * @constructor
 * @augments Tool
 * @class Allows dragging series data by mouse or touch
 * @param {Tee.Chart} chart The parent chart this tool belongs to.
 * @property {Tee.Series} [series=null] A series to be dragged, or null to drag all.
 */
let DragTool = function (chart) {
	Tool.call(this, chart)

	this.series = null

	var ta = (this.target = { series: null, index: -1 })

	this.clicked = () => {
		ta.series = null
		ta.index = -1
	}

	var p = new Point(0, 0)

	this.Point = p

	this.mousedown = function (event) {
		var s = this.chart.series.items,
			t,
			len = s.length

		this.chart.calcMouse(event, p)

		ta.series = null
		ta.index = -1

		if (this.series && this.series.visible) {
			ta.index = this.series.clicked(p)
			if (ta.index != -1) ta.series = this.series
		} else
			for (t = 0; t < len; t++) {
				if (s[t].visible) {
					ta.index = s[t].clicked(p)
					if (ta.index != -1) {
						ta.series = s[t]
						break
					}
				}
			}

		return ta.index != -1
	}

	this.mousemove = function (p) {
		if (ta.index != -1) {
			var s = ta.series,
				tmp = s.mandatoryAxis.fromPos(s.yMandatory ? p.y : p.x)

			if (this.onchanging) tmp = this.onchanging(this, tmp)

			s.data.values[ta.index] = tmp

			if (this.onchanged) this.onchanged(this, tmp)
			this.chart.draw()
		}
	}
}

DragTool.prototype = new Tool()

/**
 * @constructor
 * @augments Tool
 * @class Draws mouse draggable horizontal and / or vertical lines inside axes
 * @param {Tee.Chart} chart The parent chart this cursor tool belongs to.
 * @property {String} direction Determines if the cursor will be displayed as "vertical", "horizontal" or "both".
 * @property {Format} format Properties to control the cursor lines stroke appearance.
 * @property {Point} size The size of cursor, {x:0, y:0} means lines will cover full axes bounds.
 * @property {boolean} followMouse When true the cursor follows mouse movement over the chart.
 */
let CursorTool = function (chart) {
	Tool.call(this, chart)

	this.direction = "both" // "vertical", "horizontal", "both"
	this.size = new Point(0, 0)

	this.followMouse = true
	this.dragging = -1

	this.format = new Format(chart)

	this.horizAxis = chart ? chart.axes.bottom : null
	this.vertAxis = chart ? chart.axes.left : null

	var HIT_EPSILON = 3,
		old,
		r = new Rectangle()

	this.over = function (p) {
		var res = -1
		if (r.contains(p)) {
			var v = Math.abs(old.x - p.x) < HIT_EPSILON,
				h = Math.abs(old.y - p.y) < HIT_EPSILON,
				d = this.direction
			if (d === "both" && v && h) res = 0
			else if (v && (d === "both" || d === "vertical")) res = 1
			else if (h && (d === "both" || d === "horizontal")) res = 2
		}
		return res
	}

	this.calcRect = function () {
		var cr = chart.chartRect,
			h = this.horizAxis,
			v = this.vertAxis

		r.x = h ? h.startPos : cr.x
		r.width = h ? h.endPos - r.x : cr.width

		r.y = v ? v.startPos : cr.y
		r.height = v ? v.endPos - r.y : cr.height
	}

	var pp = new Point(0, 0)

	this.mousedown = function (p) {
		this.chart.calcMouse(p, pp)
		this.dragging = this.followMouse ? -1 : this.over(pp)
		return this.dragging > -1
	}

	this.clicked = function () {
		this.dragging = -1
	}

	this.mousemove = function (p) {
		var d = this.dragging,
			fm = this.followMouse

		if (fm || d > -1) {
			if (!old) old = new Point()

			if (old.x != p.x || old.y != p.y) {
				this.calcRect()

				if (r.contains(p)) {
					if (fm || d === 0 || d === 1) old.x = p.x
					if (fm || d === 0 || d === 2) old.y = p.y

					if (this.render == "full") this.chart.draw()
					else {
						//Restore initial canvas before drawing cursor to clean previous position

						if (canvasCopy)
							if (this.render == "copy") this.chart.ctx.drawImage(canvasCopy, 0, 0)
							else {
								var b = chart.bounds
								ctxCopy.clearRect(b.x, b.y, b.width, b.height)
							}

						this.dodraw(this.render == "copy" ? this.chart.ctx : ctxCopy)
					}

					if (this.onchange) this.onchange(p)

					return
				}
			}
		}

		var o = this.over(p)

		if (old && o > -1) {
			this.chart.newCursor = o === 0 ? "move" : o === 1 ? "e-resize" : "n-resize"
		} else this.chart.newCursor = "default"
	}

	this.render = "copy"

	var canvasCopy, ctxCopy

	this.setRender = function (r) {
		this.render = r

		if (canvasCopy) {
			this.resetCopy()
			this.chart.draw()
		}
	}

	this.resetCopy = function () {
		var ca = this.chart.canvas

		if (this.render == "layer") {
			canvasCopy.style.position = "absolute"
			ca.parentNode.appendChild(canvasCopy)

			canvasCopy.setAttribute("left", ca.offsetLeft + "px")
			canvasCopy.setAttribute("top", ca.offsetTop + "px")

			canvasCopy.style.left = ca.offsetLeft
			canvasCopy.style.top = ca.offsetTop

			canvasCopy.style.zIndex = 10
			canvasCopy.style.pointerEvents = "none"
		} else if (canvasCopy.parentNode) canvasCopy.parentNode.removeChild(canvasCopy)
	}

	this.draw = function () {
		if (this.render == "full") this.dodraw(this.chart.ctx)
		else {
			if (!canvasCopy) {
				canvasCopy = this.chart.canvas.cloneNode()
				this.resetCopy()
				ctxCopy = canvasCopy.getContext("2d") // ,{alpha:false} (opaque canvas)
			}

			if (this.render == "copy") {
				//var b=chart.bounds;
				//ctxCopy.clearRect(b.x,b.y,b.width,b.height);
				ctxCopy.drawImage(chart.canvas, 0, 0)

				this.dodraw(this.chart.ctx)
			} else this.dodraw(ctxCopy)
		}
	}

	this.dodraw = function (c) {
		var d = this.direction,
			both = d === "both",
			p

		this.calcRect()

		if (!old) old = new Point(r.x + 0.5 * r.width, r.y + 0.5 * r.height)

		c.beginPath()

		if (both || d === "vertical") {
			p = this.size.y * 0.5
			c.moveTo(old.x, p === 0 ? r.y : old.y - p)
			c.lineTo(old.x, p === 0 ? r.y + r.height : old.y + p)
		}

		if (both || d === "horizontal") {
			p = this.size.x * 0.5
			c.moveTo(p === 0 ? r.x : old.x - p, old.y)
			c.lineTo(p === 0 ? r.x + r.width : old.x + p, old.y)
		}

		this.format.stroke.prepare(this.format.stroke.fill, c)
		c.stroke()
	}
}

CursorTool.prototype = new Tool()

/**
 * @constructor
 * @augments Tool
 * @class Shows an annotation when mouse is over a series data point
 * @param {Tee.Chart} chart The parent chart this tooltip belongs to.
 * @property {Boolean} [autoHide=false] When true, the tooltip is automatically removed after "delay" milliseconds.
 * @property {Number} [delay=1000] Amount of milliseconds to wait before removing the last displayed tooltip (when "autoHide" is true).
 * @property {Number} [animated=100] Duration in milliseconds to animate the movement of tooltip from old to new position.
 * @property {Boolean} [findPoint=false] When true, the tooltip jumps to the nearest point.
 * @property {Boolean} [realTime=false] When true, unmoved tip provides live updates on changing point values.
 */
let ToolTip = function (chart) {
	Annotation.call(this, chart)
	this.pointer = {
		fill: "Green",
		firstCircleRadius: "2",
		secondCircleRadius: "5",
		visible: false,
		firstCircleOpacity: "1",
		secondCircleOpacity: "0.4",
		animationVisible: true,
		animationDuration: 200
	}
	this.visible = false
	this.findPoint = false

	/**
	 * @private
	 */
	this.currentSeries = null
	/**
	 * @private
	 */
	this.currentIndex = -1

	this.realTime = false
	/**
	 * @private
	 */
	this.timID = null

	this.autoHide = false
	this.delay = 1000
	this.animated = 100
	this.autoRedraw = true
	this.render = "dom"
	this.domStyle = "padding:5px; margin-left:5px; background-color:#FFF; border-radius:4px 4px; color:#222;"

	this.hide = async function () {
		var isDom = this.render === "dom"

		if (this.visible || isDom) {
			if (this.onhide) this.onhide(this)

			this.visible = false

			if (this.autoRedraw)
				if (isDom) Tee.DOMTip.hide()
				else this.chart.draw()

			this.currentIndex = -1
			this.currentSeries = null
		}
	}

	var redraw = args => {
		if (args) args[0].hide()
	}

	this.mousemove = async function (p) {
		var li = this.chart.series,
			len = li.count(),
			ser = null,
			index = -1
		if (this.chart.chartRect.contains(p))
			for (var t = len - 1; t >= 0; t--) {
				var s = li.items[t]

				if (s.visible) {
					index = s.clicked(p)
					if (index == -1 && s.continuous) {
						index = Math.round(this.chart.axes.bottom.fromSizeCalcIndex(p.x - this.chart.axes.bottom.startPos))
						var distance, oldDistance
						for (var n = 0; n < len; n++) {
							distance = Math.abs(li.items[n].data.values[index] - this.chart.axes.left.fromPos(p.y))
							if (n == 0) {
								oldDistance = distance
								s = li.items[n]
							} else if (distance < oldDistance) {
								s = li.items[n]
							}
						}
					}

					if (index != -1) {
						ser = s
						this.currP = p
						break
					}
				}
			}
		else {
			index = -1
			this.currP = undefined
		}
		if (index == -1) {
			this.currP = undefined
			this.hide()

			this.currentIndex = -1
			this.currentSeries = null
		} else if (index != this.currentIndex || ser != this.currentSeries || this.realTime) {
			this.currentIndex = index
			this.currentSeries = ser

			if (ser) {
				await this.refresh(ser, index)

				if (this.autoHide && this.delay > 0) {
					clearTimeout(this.timID)
					this.timID = await window.setTimeout(redraw, this.delay, [this])
				}
			}
		}
	}

	var o = null

	function step() {
		function changeTo(f) {
			o.moveTo(o.oldX + f * o.deltaX, o.oldY + f * o.deltaY)
			if (o.autoRedraw) o.chart.draw()
		}

		var now = new Date().getTime(),
			f = (now - o.init) / o.animated

		if (f < 1) {
			changeTo(f)
			requestAnimationFrame(step, o)
		} else changeTo(1)
	}

	this.refresh = async function (series, index) {
		var isDom = this.render === "dom"
		this.visible = !isDom

		this.text = series.markText(index)

		if (this.ongettext) this.text = await this.ongettext(this, this.text, series, index)

		if (this.text !== "") {
			this.resize()

			var p = new Point()
			series.calc(index, p)

			p.x -= this.bounds.width * 0.5
			p.y -= 1.5 * this.bounds.height

			if (p.x < 0) p.x = 0
			if (p.y < 0) p.y = 0

			if (!isDom && !this.autoHide && this.animated > 0 && !isNaN(this.position.x) && !isNaN(this.position.y)) {
				this.oldX = this.position.x
				this.oldY = this.position.y

				this.deltaX = p.x - this.oldX
				this.deltaY = p.y - this.oldY

				this.init = new Date().getTime()
				o = this
				requestAnimationFrame(step, this)
			} else {
				this.moveTo(p.x, p.y)

				if (this.autoRedraw)
					if (isDom) Tee.DOMTip.show(this.text, "auto", this.chart.canvas, this.domStyle, this)
					else this.chart.draw()
			}

			if (this.onshow) this.onshow(this, series, index)
		}
	}
}

ToolTip.prototype = new Annotation()

/**
 * @memberOf Tee.Chart
 * @constructor
 * @augments Annotation
 * @class Displays text at top or bottom chart sides
 * @param {Tee.Chart} chart The parent chart this title object belongs to.
 * @param {Color} fontColor The color to fill the title text.
 * @param {Boolean} [expand=false] When true, title background is aligned to panel.
 */
let Title = function Title(chart, fontColor) {
	Annotation.call(this, chart)
	this.transparent = true

	this._expand = false

	if (obDefP)
		obDefP(this, "expand", {
			get: function () {
				return this._expand
			},
			set: function (value) {
				this._expand = value
				if (!this._expand) {
					var ff = this.format
					ff.round.x = 8
					ff.round.y = 8
					ff.round.corners = null
					ff.stroke.fill = "black"
				}
			}
		})
	else this._expand = this.expand = false

	var f = this.format.font,
		s = f.shadow,
		p = this.position
	s.visible = true
	s.width = 2
	s.height = 2
	s.blur = 8

	f.style = "18px Tahoma"
	f.fill = fontColor

	this.padding = 4

	this.calcRect = function (fromTop) {
		this.resize()

		var h = this.transparent ? 1 : 2,
			b = this.bounds,
			size = b.height + h * this.padding,
			r = chart.chartRect

		if (fromTop) {
			p.y = r.y

			if (r.automatic) r.setTop(r.y + size)
		} else {
			p.y = r.y + r.height - b.height - this.padding

			if (r.automatic) r.height -= size
		}

		if (r.height < 0) r.height = 0

		p.x = 0.5 * (chart.canvas.width - b.width)
	}

	this.tryDraw = function (top) {
		if (this.shouldDraw()) {
			this.calcRect(top)

			var b = this.bounds,
				ctx = this.chart.ctx,
				groups = ctx.beginParent

			this.visual = groups ? ctx.beginParent() : null

			if (this._expand) {
				var f = chart.panel.format
				p.x = f.stroke.fill !== "" ? f.stroke.size : 0
				p.y = top ? p.x : chart.canvas.height - (f.shadow.visible ? f.shadow.height : 0) - p.x - b.height //+1; <-- only when panel border round?

				b.width = chart.canvas.width - (f.shadow.visible ? f.shadow.width : 0) - 2 * p.x

				var ff = this.format
				ff.round.x = f.round.x
				ff.round.y = f.round.y
				ff.round.corners = [top, top, !top, !top]
				ff.stroke.fill = ""

				this.transparent = false
			}

			b.x = p.x
			b.y = p.y

			this.doDraw()

			if (groups) ctx.endParent()
		}
	}
}

Title.prototype = new Annotation()

/**
 * @constructor
 * @augments Tee.Animation
 * @class Fades in/out chart elements.
 */
let FadeAnimation = function (target) {
	Animation.call(this, target)

	this.kind = "in" // in, out

	var fa

	this.fade = {}

	this.setTransp = value => {
		if (this.kind == "out") value = 1 - value

		if (fa.legend) this.chart.legend.format.transparency = value

		if (fa.walls) this.chart.walls.transparency = value

		if (fa.series)
			this.chart.series.each(s => {
				s.format.transparency = value
			})

		if (fa.marks)
			this.chart.series.each(s => {
				s.marks.transparency = value
			})

		if (fa.title) this.chart.title.format.transparency = value

		if (fa.axes) this.chart.axes.transparency = value

		if (fa.panel) this.chart.panel.format.transparency = value
	}

	this.start = function () {
		fa = this.fade
		this.setTransp(1)
	}
	this.stop = function () {
		this.setTransp(0)
	}
	this.doStep = f => {
		this.setTransp(1 - f)
	}
}

FadeAnimation.prototype = new Animation()

/**
 * @constructor
 * @augments Tee.Animation
 * @class Animates series data
 * @property {Tee.Series} series Optional Tee.Series object to animate. When null, all series and axes are animated.
 * @property {String} [kind="axis"] Animation style. Can be: axis, left, top, right, bottom, x, y, each, all, zoomin, zoomout.
 */
let SeriesAnimation = function (target) {
	Animation.call(this, target)

	if (isSeriesTarget(target)) {
		this.series = target
		this.chart = target.chart
	} else this.series = null

	this.oldmin = 0
	this.oldmax = 0
	this.oldauto = true

	var scaling = 1,
		o = this

	this.kind = "axis" // "left", "right", "top", "bottom", "axis", "x", "y", "zoomin", "zoomout", "each", "all"

	function changeAxis(o, a, amount) {
		a.automatic = false
		var mid = (o.oldmin + o.oldmax) * 0.5,
			range = (o.oldmax - o.oldmin) * 0.5
		a.maximum = mid + amount * range
		a.minimum = mid - amount * range
	}

	/**
	 * @returns {Tee.Axis} Returns the mandatory axis of the animated series, or null
	 * if no visible series exist.
	 */
	this.getAxis = function () {
		var s = this.series || this.chart.series.firstVisible()
		return s ? s.mandatoryAxis : null
	}

	this.getOtherAxis = function () {
		var s = this.series || this.chart.series.firstVisible()
		if (s) {
			if (s.yMandatory) {
				if (s.vertAxis === "both") return this.chart.axes.right
			} else if (s.horizAxis === "both") return this.chart.axes.top
		} else return null
	}

	this.doStep = f => {
		var a = o.getAxis(),
			a2 = o.getOtherAxis()
		if (a) {
			a.automatic = false
		}
		if (a2) {
			a2.automatic = false
		}

		if (o.kind == "axis") {
			changeAxis(o, a, 1 + (1 - f) * 100)
			if (a2) changeAxis(o, a2, 1 + (1 - f) * 100)
		} else
			o.chart.series.each(s => {
				if (o.series && o.series !== s) return

				var v = s.data.values,
					old = s.data._old,
					t,
					len = v.length

				if (s instanceof Tee.ActivityGauge) {
					s.maxDrawWidth = s.maxWidth * f
				} else if (s instanceof Tee.Pie) {
					s.rotation = 360 * (1 - f)
					scaling = f
				} else if (o.kind == "each") {
					var stepf = trunc(len * f)

					for (t = 0; t < stepf; t++) v[t] = old[t]

					if (stepf < len) v[stepf] = old[stepf] * (len * f - stepf)
				} else if (o.kind == "all") {
					for (t = 0; t < len; t++) v[t] = old[t] * f
				} else if (o.kind != "axis") {
					scaling = f
				}
			})
	}

	this.stop = function () {
		this.doStep(1)
		var a = o.getAxis(),
			a2 = o.getOtherAxis()

		if (a) {
			a.maximum = o.oldmax
			a.minimum = o.oldmin
			a.automatic = o.oldauto
		}
		if (a2) {
			a2.maximum = o.oldmax
			a2.minimum = o.oldmin
			a2.automatic = o.oldauto
		}

		o.chart.series.each(s => {
			if (s.transform) s.transform = null

			if (o.kind == "each" || o.kind == "all")
				if (s.data._old) {
					s.data.values = s.data._old
					s.data._old = null
				}
		})
	}

	this.start = function () {
		var a = this.getAxis(),
			a2 = this.getOtherAxis(),
			c = this.chart,
			ss = c.series.items,
			w = c.chartRect.width,
			h = c.chartRect.height,
			t,
			s,
			ww = c.bounds.width,
			hh = c.bounds.height

		if (ss.length === 0) return false

		this.oldmin = a.minimum
		this.oldmax = a.maximum
		this.oldauto = a.automatic

		for (t = 0; t < ss.length; t++) {
			s = ss[t]

			if (this.series && this.series !== s) continue

			if (s instanceof Tee.Pie)
				s.transform = function () {
					this.chart.ctx.scale(scaling, scaling)
				}
			else if (this.kind == "each" || this.kind == "all") {
				var v = s.data.values,
					tt,
					len = v.length
				s.data._old = v.slice(0)
				for (tt = 0; tt < len; tt++) v[tt] = 0
				a.automatic = false
				if (a2) a2.automatic = false
			} else if (this.kind == "left")
				s.transform = function () {
					this.chart.ctx.translate(-w * (1 - scaling), 0)
				}
			else if (this.kind == "right")
				s.transform = function () {
					this.chart.ctx.translate(w * (1 - scaling), 0)
				}
			else if (this.kind == "x")
				s.transform = function () {
					this.chart.ctx.scale(scaling, 1)
				}
			else if (this.kind == "y")
				s.transform = function () {
					this.chart.ctx.scale(1, scaling)
				}
			else if (this.kind == "top")
				s.transform = function () {
					this.chart.ctx.translate(0, -h * (1 - scaling))
				}
			else if (this.kind == "bottom")
				s.transform = function () {
					this.chart.ctx.translate(0, h * (1 - scaling))
				}
			else if (this.kind == "zoomin")
				s.transform = function () {
					var ctx = this.chart.ctx
					ctx.translate(ww * 0.5, hh * 0.5)
					ctx.scale(scaling, scaling)
					ctx.translate(-ww * 0.5, -hh * 0.5)
				}
			else if (this.kind == "zoomout")
				s.transform = function () {
					var ctx = this.chart.ctx
					ctx.translate(ww * 0.5, hh * 0.5)
					ctx.scale(2 - scaling, 2 - scaling)
					ctx.translate(-ww * 0.5, -hh * 0.5)
				}
		}

		if (this.kind == "axis") {
			changeAxis(this, a, 100)
			if (a2) changeAxis(this, a2, 100)
		}
	}
}

SeriesAnimation.prototype = new Animation()

/**
 * @memberOf Tee.Series
 * @constructor
 * @augments Annotation
 * @class Formatting properties to display annotations near series data points
 * @param {Tee.Series} series The parent series this marks object belongs to.
 * @param {Tee.Chart} chart The parent chart this marks object belongs to.
 * @property {Format} arrow Displays a line from mark to corresponding series point.
 * @property {Number} [arrow.length=10] Distance in pixels from mark to corresponding series point.
 * @property {Boolean} [arrow.underline=false] Draws a line under mark text.
 * @property {String} [style="auto"] Determines the text to display inside mark.
 * @property {Boolean} visible Defines if series marks are to be displayed or not.
 * @property {Number} [drawEvery=1] Controls how many marks to skip in between. (Useful for large series).
 */
let Marks = function Marks(series, chart) {
	Annotation.call(this, chart)
	this.series = series

	var arrow = (this.arrow = new Format(chart))
	arrow.visible = true
	arrow.length = 10
	arrow.underline = false
	arrow.z = 0.5
	arrow.depth = 0.1

	this.style = "auto" // "value", "percent", "label", "valuelabel", "percentlabel" ...

	this.drawEvery = 1
	this.visible = false
	this.format.z = 0.5

	/*
		* @private
		*/
	this.setChart = function (chart) {
		this.chart = chart
		this.format.setChart(chart)
		arrow.setChart(chart)
	}

	this.drawPolar = function (center, radius, angle, index) {
		var text = this.series.markText(index),
			px = center.x + Math.cos(angle) * radius,
			py = center.y + Math.sin(angle) * radius,
			c = this.chart.ctx

		this.text = text
		this.resize()

		var b = this.bounds,
			p2x,
			p2y,
			p = this.position

		radius += arrow.length
		;(p2x = center.x + Math.cos(angle) * radius), (p2y = center.y + Math.sin(angle) * radius)

		if (p2x - b.width < 0) p2x -= p2x - b.width - 4

		if (Math.abs(p2x - center.x) < b.width) p.x = p2x - b.width * 0.5
		else p.x = p2x < center.x ? p2x - b.width : p2x

		if (Math.abs(p2y - center.y) < b.height) p.y = p2y - b.height * 0.5
		else p.y = p2y < center.y ? p2y - b.height : p2y

		c.beginPath()
		c.moveTo(px, py)
		c.lineTo(p2x, p2y)

		if (arrow.underline) {
			if (p2y <= p.y || p2y >= p.y + b.height) {
				c.moveTo(p.x, p2y)
				c.lineTo(p.x + b.width, p2y)
			}
		}

		arrow.stroke.prepare()
		if (arrow.visible) c.stroke()

		this.draw()
	}

	this.canDraw = function (x, y, index, inverted) {
		var s = this.series.markText(index)

		if (s && s !== "" && (this.showZero || s !== "0")) {
			this.text = s
			this.resize()

			var factor = inverted ? -1 : 1,
				r = this.bounds,
				m = this.series.yMandatory

			if (m) {
				r.x = x - r.width * 0.5
				r.y = y - factor * (arrow.length + (inverted ? 0 : r.height))
			} else {
				r.x = x + factor * arrow.length
				if (inverted) r.x -= r.width
				r.y = y - r.height * 0.5
			}

			this.position.x = r.x
			this.position.y = r.y

			return true
		} else return false
	}

	this.drawMark = function (x, y, index, inverted) {
		if (this.canDraw(x, y, index, inverted)) {
			this.draw()

			if (arrow.visible) {
				var r = this.bounds,
					m = this.series.yMandatory

				var rbot = inverted ? r.y : r.getBottom(),
					c = this.chart.ctx,
					is3d = this.chart.aspect.view3d

				if (m) {
					if (is3d) {
						var rr = { x: x - 3, y: rbot, width: 6, height: y - rbot }

						arrow.z = this.format.z - arrow.depth * 0.5

						arrow.cylinder(rr, 1, true)
						arrow.draw(this.chart.ctx, null, rr)

						return
					} else {
						c.beginPath()

						c.moveTo(x, rbot)
						c.lineTo(x, y)

						if (arrow.underline) {
							c.moveTo(r.x, rbot)
							c.lineTo(r.x + r.width, rbot)
						}
					}
				} else {
					var py = r.y + r.height * 0.5

					c.beginPath()
					c.moveTo(x, py)

					if (inverted) r.x += r.width
					c.lineTo(r.x, py)

					if (arrow.underline) {
						c.moveTo(r.x, r.y + r.height)
						c.lineTo(r.x + (inverted ? -r.width : r.width), r.y + r.height)
					}
				}

				arrow.stroke.prepare()
				c.stroke()
			}
		}
	}
}

Marks.prototype = new Annotation()

export { AnimateHover, Animation, Annotation, CursorTool, DragTool, FadeAnimation, Marks, SeriesAnimation, Title, Tool, ToolTip }

